const SESSION_WAIT_MS = 15000;
const PAGE_GRACE_MS = 10000;
const OPEN_EVENT_TIMEOUT_MS = 4500;
const ELIGIBILITY_POLL_MS = 1000;
const MAX_TIMEOUT_MS = 2147483647;
const HANDLED_EVENT_TYPES = new Set([
  'open',
  'close',
  'submit',
  'stepSubmit',
  'redirectedToUrl',
]);

const fallback = document.querySelector('[data-launch-wheel-fallback]');

if (fallback instanceof HTMLElement) {
  const formId = fallback.dataset.formId;
  const cutoffAt = Date.parse(fallback.dataset.cutoff || '');
  const pageStartedAt = Date.now();
  const openButton = fallback.querySelector('[data-launch-wheel-open]');
  const dismissButton = fallback.querySelector('[data-launch-wheel-dismiss]');
  const status = fallback.querySelector('[data-launch-wheel-status]');
  const handledStorageKey = `sigma:launch-wheel-fallback:${formId}:handled`;
  const dismissedStorageKey = `sigma:launch-wheel-fallback:${formId}:dismissed`;
  const sessionStartStorageKey = `sigma:launch-wheel-fallback:${formId}:session-start`;
  let campaignHandled = false;
  let focusReturnTarget;
  let requestTimeout;
  let pollInterval;
  let cutoffTimeout;
  let priorityObserver;
  let eligibilityActive = false;

  const readStorage = (storageType, key) => {
    try {
      return { available: true, value: window[storageType].getItem(key) };
    } catch (error) {
      return { available: false, value: null };
    }
  };

  const writeStorage = (storageType, key, value) => {
    try {
      window[storageType].setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  };

  const isExpired = () => !Number.isFinite(cutoffAt) || Date.now() >= cutoffAt;
  const isMarketingAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
    } catch (error) {
      return false;
    }
  };

  const isSurfaceVisible = (surface) => {
    if (!(surface instanceof HTMLElement) || surface.hidden || surface.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    try {
      const styles = window.getComputedStyle(surface);
      return styles.display !== 'none' && styles.visibility !== 'hidden' && surface.getClientRects().length > 0;
    } catch (error) {
      return true;
    }
  };

  const hasHigherPrioritySurface = () =>
    (() => {
      const internationalNotice = document.querySelector('[data-international-tax-notice]');
      if (internationalNotice instanceof HTMLElement && !internationalNotice.hidden) return true;

      return [
        document.querySelector('#shopify-pc__banner'),
        ...document.querySelectorAll('#shopify-pc__prefs'),
        ...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]'),
      ].some(isSurfaceVisible);
    })();

  const stopEligibilityChecks = () => {
    eligibilityActive = false;
    window.clearInterval(pollInterval);
    priorityObserver?.disconnect();
  };

  const clearRequest = () => {
    const requestWasActive =
      requestTimeout !== undefined ||
      openButton?.hasAttribute('aria-busy') ||
      (openButton instanceof HTMLButtonElement && openButton.disabled);

    window.clearTimeout(requestTimeout);
    requestTimeout = undefined;

    if (!requestWasActive) return;

    openButton?.removeAttribute('aria-busy');

    if (openButton instanceof HTMLButtonElement) {
      openButton.disabled = false;
      openButton.textContent = 'Spin to win';
    }
  };

  const hide = ({ restoreFocus = false } = {}) => {
    if (!fallback.hidden) fallback.hidden = true;
    clearRequest();

    if (restoreFocus && fallback.contains(document.activeElement)) {
      const target = focusReturnTarget?.isConnected
        ? focusReturnTarget
        : document.querySelector('#MainContent');
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }
  };

  const showStatus = (message, { cookieSettings = false } = {}) => {
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    if (cookieSettings) {
      const label = 'Cookie settings';
      const index = message.indexOf(label);
      if (index !== -1) {
        const link = document.createElement('a');
        link.textContent = label;
        link.href = document.querySelector('a[data-cookie-settings]')?.getAttribute('href') ||
          '/policies/privacy-policy#shopifyReshowConsentBanner';
        link.dataset.cookieSettings = '';
        link.className = 'launch-wheel-fallback__settings';
        status.replaceChildren(message.slice(0, index), link, message.slice(index + label.length));
      }
    }
    status.hidden = false;
  };

  const rememberCampaignInteraction = () => {
    campaignHandled = true;
    writeStorage('localStorage', handledStorageKey, '1');
    stopEligibilityChecks();
    hide();
  };

  const sessionStartResult = readStorage('sessionStorage', sessionStartStorageKey);
  let sessionStartedAt = Number.parseInt(sessionStartResult.value || '', 10);

  if (sessionStartResult.available && !Number.isFinite(sessionStartedAt)) {
    sessionStartedAt = pageStartedAt;
    if (!writeStorage('sessionStorage', sessionStartStorageKey, String(sessionStartedAt))) {
      sessionStartedAt = Number.NaN;
    }
  }

  const canReveal = () => {
    // A loaded API can coexist with blocked form requests. Only a matching
    // form event proves this visitor has actually seen the campaign.
    if (campaignHandled || isExpired()) return false;

    const handled = readStorage('localStorage', handledStorageKey);
    const dismissed = readStorage('sessionStorage', dismissedStorageKey);

    if (!handled.available || !dismissed.available || !Number.isFinite(sessionStartedAt)) return false;
    if (handled.value === '1' || dismissed.value === '1') return false;
    if (Date.now() - sessionStartedAt < SESSION_WAIT_MS) return false;
    if (Date.now() - pageStartedAt < PAGE_GRACE_MS) return false;
    // This native help card does not load the provider or grant consent.
    if (hasHigherPrioritySurface()) return false;

    return true;
  };

  const syncEligibility = () => {
    if (canReveal()) {
      if (fallback.hidden) fallback.hidden = false;
      return;
    }

    hide();
    if (campaignHandled || isExpired()) {
      stopEligibilityChecks();
    }
  };

  const scheduleCutoff = () => {
    window.clearTimeout(cutoffTimeout);

    if (isExpired()) {
      hide();
      return;
    }

    cutoffTimeout = window.setTimeout(
      () => {
        hide();
        stopEligibilityChecks();
      },
      Math.min(cutoffAt - Date.now(), MAX_TIMEOUT_MS),
    );
  };

  const matchingFormEvent = (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    const eventFormId = detail?.formId || detail?.formID;
    return eventFormId === formId && HANDLED_EVENT_TYPES.has(detail?.type);
  };

  window.addEventListener('klaviyoForms', (event) => {
    if (!matchingFormEvent(event)) return;
    rememberCampaignInteraction();
  });

  if (
    formId &&
    openButton instanceof HTMLButtonElement &&
    dismissButton instanceof HTMLButtonElement &&
    status instanceof HTMLElement &&
    Number.isFinite(cutoffAt) &&
    sessionStartResult.available
  ) {
    const handled = readStorage('localStorage', handledStorageKey);
    const dismissed = readStorage('sessionStorage', dismissedStorageKey);

    if (handled.available && dismissed.available && handled.value !== '1' && dismissed.value !== '1' && !isExpired()) {
      eligibilityActive = true;
      syncEligibility();
      if (eligibilityActive) {
        pollInterval = window.setInterval(syncEligibility, ELIGIBILITY_POLL_MS);
      }
      scheduleCutoff();
    }

    fallback.addEventListener('focusin', (event) => {
      if (!fallback.contains(event.relatedTarget) && event.relatedTarget instanceof HTMLElement) {
        focusReturnTarget = event.relatedTarget;
      }
    });

    openButton.addEventListener('click', () => {
      if (isExpired() || hasHigherPrioritySurface()) {
        syncEligibility();
        return;
      }

      if (!isMarketingAllowed()) {
        showStatus(
          'To open the wheel, allow marketing cookies in Cookie settings. If your blocker hides those settings or the wheel, allow this site in your blocker settings, then reload and try again.',
          { cookieSettings: true },
        );
        return;
      }

      const klaviyoOnsite = window._klOnsite;

      if (!klaviyoOnsite || typeof klaviyoOnsite.openForm !== 'function') {
        showStatus(
          'The wheel couldn’t load. Your ad or tracker blocker may be preventing it. Allow this site in your blocker settings, then reload and try again.',
        );
        return;
      }

      status.hidden = true;
      openButton.disabled = true;
      openButton.setAttribute('aria-busy', 'true');
      openButton.textContent = 'Opening…';

      try {
        klaviyoOnsite.openForm([formId]);
      } catch (error) {
        clearRequest();
        showStatus(
          'The wheel couldn’t load. Your ad or tracker blocker may be preventing it. Allow this site in your blocker settings, then reload and try again.',
        );
        return;
      }

      if (campaignHandled || isExpired()) {
        hide();
        return;
      }

      requestTimeout = window.setTimeout(() => {
        if (campaignHandled || isExpired()) {
          hide();
          return;
        }

        clearRequest();
        showStatus(
          'The wheel couldn’t load. Your ad or tracker blocker may be preventing it. Allow this site in your blocker settings, then reload and try again.',
        );
      }, OPEN_EVENT_TIMEOUT_MS);
    });

    dismissButton.addEventListener('click', () => {
      writeStorage('sessionStorage', dismissedStorageKey, '1');
      stopEligibilityChecks();
      hide({ restoreFocus: true });
    });

    const recheckEligibility = () => {
      syncEligibility();
      scheduleCutoff();
    };

    window.addEventListener('focus', recheckEligibility);
    window.addEventListener('pageshow', recheckEligibility);
    document.addEventListener('visitorConsentCollected', recheckEligibility);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') recheckEligibility();
    });

    if (eligibilityActive && typeof MutationObserver === 'function' && document.body) {
      priorityObserver = new MutationObserver(syncEligibility);
      priorityObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'style', 'class', 'open', 'aria-hidden', 'aria-modal'],
      });
    }
  }
}
